import * as XLSX from "xlsx";
import { t } from "@lingui/core/macro";
import type { ColumnDef } from "@tanstack/react-table";
import { recipeRequests } from "@/lib/api/requests/recipes";
import type { CreateRecipeInput } from "@/lib/api/requests/recipes";
import type { Recipe, RecipeType } from "@/types/recipe";
import { materialApi } from "@/lib/api/services/material.api";
import type { BulkCreateMaterialItem, BulkSubIngredientItem, Material } from "@/types/material";

// ---------------------------------------------------------------------------
// Draft types — used in the review step before recipes are created
// ---------------------------------------------------------------------------

export interface DraftRecipeItem {
  /** Stable local key for React rendering and updates. */
  localId: string;
  name: string;
  slug: string;
  /**
   * Which sheet the row came from decides this: the Prep Recipes sheet makes
   * components, the Menu Items sheet makes dishes (T-701).
   */
  recipeType: RecipeType;
  category?: string;
  subcategory?: string;
  description?: string;
  difficulty?: "EASY" | "MEDIUM" | "HARD";
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  servings?: number;
  /** Allergen text captured from the imported file — display only; not sent to CreateRecipeInput. */
  allergenText?: string;
}

export interface DraftIngredientItem {
  /** Stable local key for React rendering and updates. */
  localId: string;
  name: string;
  unitOfMeasure: string;
  type?: "RAW" | "COMPOUND" | "READY_TO_USE";
  sku?: string;
  unitCost?: number;
  unitCostUnit?: string;
  description?: string;
  imageUrl?: string;
  caloriesPerUnit?: number;
  proteinG?: number;
  carbsG?: number;
  fatG?: number;
  fiberG?: number;
  sodiumMg?: number;
  sugarG?: number;
  /** Display string rendered in the review step (e.g. "GLUTEN, DAIRY"). */
  allergenText?: string;
  /** Allergen codes parsed from allergenText — sent to the API at import time. */
  allergenCodes?: string[];
  /** Sub-ingredients parsed from the Sub-Ingredients sheet — sent to the API at import time. */
  subIngredients?: BulkSubIngredientItem[];
}

export interface ParsedImportResult {
  menuItems: DraftRecipeItem[];
  prepRecipes: DraftRecipeItem[];
  ingredients: DraftIngredientItem[];
}

export interface ImportRecipeError {
  row: number;
  name: string;
  error: string;
}

export interface ImportResult {
  created: number;
  failed: ImportRecipeError[];
}

export interface ImportRecipesOptions {
  file: File;
  menuItemColumns: ColumnDef<Recipe, unknown>[];
  prepRecipeColumns: ColumnDef<Recipe, unknown>[];
  onProgress?: (current: number, total: number) => void;
}

// Fields from Recipe type that map directly to CreateRecipeInput
const IMPORTABLE_FIELDS = new Set<keyof Recipe>([
  "name",
  "slug",
  "category",
  "subcategory",
  "description",
  "difficulty",
  "prepTimeMinutes",
  "cookTimeMinutes",
  "servings",
]);

const INT_FIELDS = new Set<string>(["prepTimeMinutes", "cookTimeMinutes", "servings"]);

// Build a label→field map from export column definitions, using only columns
// with an accessorKey (not accessorFn) so the mapping is lossless.
function buildHeaderMap(
  columns: ColumnDef<Recipe, unknown>[],
): Map<string, keyof Recipe> {
  const map = new Map<string, keyof Recipe>();
  for (const col of columns) {
    const label = col.meta?.label as string | undefined;
    const key = (col as { accessorKey?: keyof Recipe }).accessorKey;
    if (label && key && IMPORTABLE_FIELDS.has(key)) {
      map.set(label, key);
    }
  }
  return map;
}

function parseSheet(
  sheet: XLSX.WorkSheet,
  headerMap: Map<string, keyof Recipe>,
): CreateRecipeInput[] {
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
  });

  const recipes: CreateRecipeInput[] = [];

  for (const row of rows) {
    const recipe: Partial<CreateRecipeInput> = {};

    for (const [header, field] of headerMap) {
      if (!(header in row)) continue;
      const val = row[header];
      if (val === "" || val === null || val === undefined) continue;

      if (INT_FIELDS.has(field as string)) {
        const n = Number(val);
        if (!isNaN(n) && n >= 0) {
          (recipe as Record<string, unknown>)[field as string] = Math.round(n);
        }
      } else {
        (recipe as Record<string, unknown>)[field as string] = String(val).trim();
      }
    }

    if (recipe.name && recipe.slug) {
      recipes.push(recipe as CreateRecipeInput);
    }
  }

  return recipes;
}

const MENU_ITEMS_SHEET = "Menu Items";
const PREP_RECIPES_SHEET = "Prep Recipes";
const INGREDIENTS_SHEET = "Ingredients";
const SUB_INGREDIENTS_SHEET = "Sub-Ingredients";

// ---------------------------------------------------------------------------
// Ingredient-specific header parsing
// ---------------------------------------------------------------------------

const IMPORTABLE_MATERIAL_FIELDS = new Set<keyof Material>([
  "name",
  "sku",
  "type",
  "unitOfMeasure",
  "unitCost",
  "unitCostUnit",
  "description",
  "imageUrl",
  "caloriesPerUnit",
  "proteinG",
  "carbsG",
  "fatG",
  "fiberG",
  "sodiumMg",
  "sugarG",
]);

const FLOAT_FIELDS = new Set<string>([
  "unitCost",
  "caloriesPerUnit",
  "proteinG",
  "carbsG",
  "fatG",
  "fiberG",
  "sodiumMg",
  "sugarG",
]);

function buildIngredientHeaderMap(
  columns: ColumnDef<Material, unknown>[],
): Map<string, keyof Material> {
  const map = new Map<string, keyof Material>();
  for (const col of columns) {
    const label = col.meta?.label as string | undefined;
    const key = (col as { accessorKey?: keyof Material }).accessorKey;
    if (label && key && IMPORTABLE_MATERIAL_FIELDS.has(key)) {
      map.set(label, key);
    }
  }
  return map;
}

function parseRowToIngredient(
  row: Record<string, unknown>,
  headerMap: Map<string, keyof Material>,
): Omit<BulkCreateMaterialItem, "allergenCodes" | "subIngredients"> | null {
  const ingredient: Record<string, unknown> = {};

  for (const [header, field] of headerMap) {
    if (!(header in row)) continue;
    const val = row[header];
    if (val === "" || val === null || val === undefined) continue;

    if (FLOAT_FIELDS.has(field as string)) {
      const n = Number(val);
      if (!isNaN(n) && n >= 0) {
        ingredient[field as string] = n;
      }
    } else {
      ingredient[field as string] = String(val).trim();
    }
  }

  return ingredient["name"] && ingredient["unitOfMeasure"]
    ? (ingredient as unknown as Omit<BulkCreateMaterialItem, "allergenCodes" | "subIngredients">)
    : null;
}

// ---------------------------------------------------------------------------
// Row-level parser (extracted from parseSheet for reuse)
// ---------------------------------------------------------------------------

function parseRowToRecipe(
  row: Record<string, unknown>,
  headerMap: Map<string, keyof Recipe>,
): CreateRecipeInput | null {
  const recipe: Partial<CreateRecipeInput> = {};

  for (const [header, field] of headerMap) {
    if (!(header in row)) continue;
    const val = row[header];
    if (val === "" || val === null || val === undefined) continue;

    if (INT_FIELDS.has(field as string)) {
      const n = Number(val);
      if (!isNaN(n) && n >= 0) {
        (recipe as Record<string, unknown>)[field as string] = Math.round(n);
      }
    } else {
      (recipe as Record<string, unknown>)[field as string] = String(val).trim();
    }
  }

  return recipe.name && recipe.slug ? (recipe as CreateRecipeInput) : null;
}

export async function importRecipesFromFile(
  options: ImportRecipesOptions,
): Promise<ImportResult> {
  const { file, menuItemColumns, prepRecipeColumns, onProgress } = options;

  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });

  const menuItemHeaderMap = buildHeaderMap(menuItemColumns);
  const prepRecipeHeaderMap = buildHeaderMap(prepRecipeColumns);

  const allRecipes: CreateRecipeInput[] = [];

  for (const sheetName of wb.SheetNames) {
    const sheet = wb.Sheets[sheetName];
    if (!sheet) continue;

    let headerMap: Map<string, keyof Recipe>;
    if (sheetName === PREP_RECIPES_SHEET) {
      headerMap = prepRecipeHeaderMap;
    } else if (sheetName === MENU_ITEMS_SHEET) {
      headerMap = menuItemHeaderMap;
    } else {
      // Unknown sheet — merge both maps (menu items columns take precedence)
      headerMap = new Map([...prepRecipeHeaderMap, ...menuItemHeaderMap]);
    }

    allRecipes.push(...parseSheet(sheet, headerMap));
  }

  const result: ImportResult = { created: 0, failed: [] };

  for (let i = 0; i < allRecipes.length; i++) {
    onProgress?.(i, allRecipes.length);
    const recipe = allRecipes[i]!;
    try {
      await recipeRequests.create(recipe);
      result.created++;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t`Unknown error`;
      result.failed.push({
        row: i + 2,
        name: recipe.name,
        error: message,
      });
    }
  }

  onProgress?.(allRecipes.length, allRecipes.length);
  return result;
}

// ---------------------------------------------------------------------------
// Two-phase import: parse first (no side-effects), then create
// ---------------------------------------------------------------------------

const MERGED_SHEET = "Recipes";

/**
 * Parses a file and returns draft recipe items grouped by type WITHOUT
 * creating anything in the API. Use `createParsedRecipes` to persist them.
 */
export async function parseImportFile(
  file: File,
  menuItemColumns: ColumnDef<Recipe, unknown>[],
  prepRecipeColumns: ColumnDef<Recipe, unknown>[],
  ingredientColumns: ColumnDef<Material, unknown>[],
): Promise<ParsedImportResult> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });

  const menuItemHeaderMap = buildHeaderMap(menuItemColumns);
  const prepRecipeHeaderMap = buildHeaderMap(prepRecipeColumns);
  const ingredientHeaderMap = buildIngredientHeaderMap(ingredientColumns);

  // Resolve the label used for the allergen column in the export
  const allergenLabel =
    (menuItemColumns.find((c) => c.id === "allergens")?.meta?.label as string | undefined) ??
    "Allergens";
  const ingredientAllergenLabel =
    (ingredientColumns.find((c) => c.id === "allergens")?.meta?.label as string | undefined) ??
    "Allergens";

  const menuItems: DraftRecipeItem[] = [];
  const prepRecipes: DraftRecipeItem[] = [];
  const ingredients: DraftIngredientItem[] = [];

  // Sub-ingredient rows are collected during parsing and linked to parent
  // ingredients in a post-processing step so sheet order doesn't matter.
  const pendingSubIngredients: Array<{
    parentName: string;
    childName: string;
    quantity: number;
    unit: string;
  }> = [];

  for (const sheetName of wb.SheetNames) {
    const sheet = wb.Sheets[sheetName];
    if (!sheet) continue;

    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: "",
    });

    if (sheetName === INGREDIENTS_SHEET) {
      for (const row of rows) {
        const ingredient = parseRowToIngredient(row, ingredientHeaderMap);
        if (!ingredient) continue;
        const allergenRaw = String(row[ingredientAllergenLabel] ?? "").trim();
        const allergenCodes = allergenRaw
          ? allergenRaw.split(",").map((c) => c.trim()).filter(Boolean)
          : undefined;
        ingredients.push({
          localId: crypto.randomUUID(),
          ...ingredient,
          allergenText: allergenRaw || undefined,
          allergenCodes,
        });
      }
      continue;
    }

    if (sheetName === SUB_INGREDIENTS_SHEET) {
      for (const row of rows) {
        const parentName = String(row["Parent Name"] ?? "").trim();
        const childName = String(row["Child Name"] ?? "").trim();
        const quantity = Number(row["Quantity"]);
        const unit = String(row["Unit"] ?? "").trim();
        if (parentName && childName && !isNaN(quantity) && quantity >= 0 && unit) {
          pendingSubIngredients.push({ parentName, childName, quantity, unit });
        }
      }
      continue;
    }

    for (const row of rows) {
      let isPrepRecipe = false;
      let headerMap: Map<string, keyof Recipe>;

      if (sheetName === MENU_ITEMS_SHEET) {
        headerMap = menuItemHeaderMap;
      } else if (sheetName === PREP_RECIPES_SHEET) {
        headerMap = prepRecipeHeaderMap;
        isPrepRecipe = true;
      } else if (sheetName === MERGED_SHEET) {
        const category = String(row["Category"] ?? "").trim();
        isPrepRecipe = category === "Prep Recipe";
        headerMap = isPrepRecipe ? prepRecipeHeaderMap : menuItemHeaderMap;
      } else {
        // Unknown sheet — skip
        break;
      }

      const recipe = parseRowToRecipe(row, headerMap);
      if (!recipe) continue;

      const item: DraftRecipeItem = {
        localId: crypto.randomUUID(),
        ...recipe,
        recipeType: isPrepRecipe ? "PREP" : "PLATED",
        allergenText: String(row[allergenLabel] ?? "").trim() || undefined,
      };

      if (isPrepRecipe) prepRecipes.push(item);
      else menuItems.push(item);
    }
  }

  // Link sub-ingredient rows to their parent ingredients by name.
  if (pendingSubIngredients.length > 0) {
    const nameToIngredient = new Map(ingredients.map((ing) => [ing.name, ing]));
    for (const { parentName, childName, quantity, unit } of pendingSubIngredients) {
      const parent = nameToIngredient.get(parentName);
      if (!parent) continue;
      if (!parent.subIngredients) parent.subIngredients = [];
      parent.subIngredients.push({ childMaterialName: childName, quantity, unit });
    }
  }

  return { menuItems, prepRecipes, ingredients };
}

// ---------------------------------------------------------------------------
// Draft → API input converters (keep draft-only fields out of the API payload)
// ---------------------------------------------------------------------------

function toRecipeInput(item: DraftRecipeItem): CreateRecipeInput {
  return {
    name: item.name,
    slug: item.slug,
    recipeType: item.recipeType,
    ...(item.category !== undefined && { category: item.category }),
    ...(item.subcategory !== undefined && { subcategory: item.subcategory }),
    ...(item.description !== undefined && { description: item.description }),
    ...(item.difficulty !== undefined && { difficulty: item.difficulty }),
    ...(item.prepTimeMinutes !== undefined && { prepTimeMinutes: item.prepTimeMinutes }),
    ...(item.cookTimeMinutes !== undefined && { cookTimeMinutes: item.cookTimeMinutes }),
    ...(item.servings !== undefined && { servings: item.servings }),
  };
}

function toMaterialInput(item: DraftIngredientItem): BulkCreateMaterialItem {
  return {
    name: item.name,
    unitOfMeasure: item.unitOfMeasure,
    ...(item.type !== undefined && { type: item.type }),
    ...(item.sku !== undefined && { sku: item.sku }),
    ...(item.unitCost !== undefined && { unitCost: item.unitCost }),
    ...(item.unitCostUnit !== undefined && { unitCostUnit: item.unitCostUnit }),
    ...(item.description !== undefined && { description: item.description }),
    ...(item.imageUrl !== undefined && { imageUrl: item.imageUrl }),
    ...(item.caloriesPerUnit !== undefined && { caloriesPerUnit: item.caloriesPerUnit }),
    ...(item.proteinG !== undefined && { proteinG: item.proteinG }),
    ...(item.carbsG !== undefined && { carbsG: item.carbsG }),
    ...(item.fatG !== undefined && { fatG: item.fatG }),
    ...(item.fiberG !== undefined && { fiberG: item.fiberG }),
    ...(item.sodiumMg !== undefined && { sodiumMg: item.sodiumMg }),
    ...(item.sugarG !== undefined && { sugarG: item.sugarG }),
    ...(item.allergenCodes?.length && { allergenCodes: item.allergenCodes }),
    ...(item.subIngredients?.length && { subIngredients: item.subIngredients }),
  };
}

/**
 * Creates recipes from the draft items returned by `parseImportFile`.
 * Uses the bulk endpoint — one HTTP request for all items.
 * Allergen data on DraftRecipeItem is display-only and is NOT sent to the API.
 */
export async function createParsedRecipes(
  items: DraftRecipeItem[],
  onProgress?: (current: number, total: number) => void,
  organizationId?: number,
): Promise<ImportResult> {
  if (items.length === 0) return { created: 0, failed: [] };

  onProgress?.(0, items.length);
  const bulk = await recipeRequests.bulkCreate(items.map(toRecipeInput), organizationId);
  onProgress?.(items.length, items.length);

  return {
    created: bulk.created,
    failed: bulk.failed.map((f) => ({
      row: f.index + 2, // +1 for header, +1 for 1-based rows
      name: f.name,
      error: f.error,
    })),
  };
}

/**
 * Creates materials (ingredients) from the draft items returned by `parseImportFile`.
 * Uses the bulk endpoint — one HTTP request for all items.
 * Allergen data on DraftIngredientItem is display-only and is NOT sent to the API.
 */
export async function createParsedIngredients(
  items: DraftIngredientItem[],
  onProgress?: (current: number, total: number) => void,
  organizationId?: number,
): Promise<ImportResult> {
  if (items.length === 0) return { created: 0, failed: [] };

  onProgress?.(0, items.length);
  const bulk = await materialApi.bulkCreate(items.map(toMaterialInput), organizationId);
  onProgress?.(items.length, items.length);

  return {
    created: bulk.created,
    failed: bulk.failed.map((f) => ({
      row: f.index + 2,
      name: f.name,
      error: f.error,
    })),
  };
}
